import { Shadows, alpha, createTheme } from "@mui/material/styles";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {
    prime: string;
    grey: string;
    red: string;
    orange: string;
    green: string;
    gradient: string;
    gradient2: string;
    gradient3: string;
    gradientSuccess: string;
    gradientSidebarLight: string;
    gradientSidebarDark: string;
    bgLight: string;
    border: string;
    white: string;
    bgDark: string;
    dark: string;
    borderDark: string;
    layer1Dark: string;
    layer2Dark: string;
    dialogDark: string;
    greenBhabua: string;
    greyDark: string;
    greyLight: string;
    nimbusCloud: string;
    cedarChest: string;
    tooGold: string;
    coldHeights: string;
    schnitzel: string;
  }

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

export const colors = {
  // The brand blue is the mascot's own: the sky it is drawn against and the
  // blue half of the SKYSTAKE wordmark. Everything else here is either that
  // hue at another lightness or a neutral tinted towards it.
  prime: "#2E90FF",
  grey: "#8A93A6",
  red: "#CF202E",
  orange: "#F7931A",
  green: "#36FF00",
  brightGreen: "#00E676",
  gradient: "linear-gradient(180deg, #2E90FF 0%, #0B63D6 100%)",
  gradient2: "linear-gradient(180deg, #A9E2FF 0%, #2E90FF 100%)",
  gradient3: "linear-gradient(0deg, #5BC8FF 0%, #0A6FE0 100%)",
  gradientSuccess: "linear-gradient(180deg, #34A853 0%, #067524 100%)",
  gradientSidebarLight:
    "linear-gradient(90deg, rgba(46, 144, 255, 0) 18.49%, rgba(11, 99, 214, 0.32) 100%)",
  gradientSidebarDark:
    "linear-gradient(90deg, rgba(46, 144, 255, 0) 0%, rgba(46, 144, 255, 0.12) 100%)",
  gradientSidebarDark2x:
    "linear-gradient(90deg, rgba(46, 144, 255, 0) 0%, rgba(46, 144, 255, 0.24) 100%)",
  // The page under the cards in the light scheme: a pale blue-grey, so a white
  // card is visibly a card. It was near-white, a shade off the cards on it.
  bgLight: "#F3F6FB",
  border: "#e2e6ee",
  white: "#ffffff",
  // The night sky the mascot stands in, and the two surfaces lifted out of it.
  bgDark: "#040A16",
  dark: "#040A16",
  borderDark: "#182942",
  // Cards and panels (layer 1) and what sits on them (layer 2). Lifted a clear
  // step out of the page and toward the mascot's blue: at #0A1322 a card was
  // near-black on a near-black page and only its border said it was there.
  layer1Dark: "#0D1B30",
  layer2Dark: "#14263F",
  // Below layer 1, so the cards, buttons and dividers inside a modal still stand out.
  dialogDark: "#0A1322",
  greenBhabua: "#C8F558",
  greyDark: "#8A93A6",
  greyLight: "#F2F4F8",
  nimbusCloud: "#C7C9CD",
  cedarChest: "#CA5A4B",
  // The mascot's crown and beak. VIP tiers and tournament podiums still want a
  // gold, and it is the one accent the artwork keeps alongside the blue.
  tooGold: "#FDC445",
  coldHeights: "#2EDFF0",
  // The light scheme's accent. `prime` is a 2.6:1 figure on a white panel, so
  // the deep end of the brand ramp stands in for it wherever ink sits on paper.
  schnitzel: "#0B63D6",

  cardBg1: "#2e90ff33",
  cardBg2: "#2e90ff00",
};

// Gradient-filled text (hero titles): the `background: <gradient> text`
// shorthand (its `text` value sets background-clip) with color: transparent.
// The stops read the wordmark left to right: cloud white into brand blue.
export const gradientTextSx = {
  background:
    "linear-gradient(120deg, rgb(199, 232, 255), rgb(46, 144, 255) 60%, rgb(11, 99, 214)) text",
  color: "transparent",
};

export const brand = {
  50: "hsl(210, 100%, 96%)",
  100: "hsl(211, 97%, 89%)",
  200: "hsl(211, 97%, 79%)",
  300: "hsl(212, 100%, 71%)",
  400: colors.prime,
  500: "hsl(213, 95%, 50%)",
  600: "hsl(214, 92%, 42%)",
  700: "hsl(215, 90%, 34%)",
  800: "hsl(216, 88%, 22%)",
  900: "hsl(217, 85%, 14%)",
};

// Neutrals carry a trace of the brand hue rather than being flat grey, so a
// panel edge or a muted label sits in the same sky as everything around it.
export const gray = {
  50: "#f2f6fc",
  100: "#e3eaf5",
  200: "#c7d4e7",
  300: "#a2b4ce",
  400: "#64799a",
  500: "#3e5273",
  600: "#24354f",
  700: "#182942",
  800: "#0a1322",
  900: "#040a16",
};

const green = {
  50: "hsl(120, 80%, 98%)",
  100: "hsl(120, 75%, 94%)",
  200: "hsl(120, 75%, 87%)",
  300: "hsl(120, 61%, 77%)",
  400: "hsl(120, 44%, 53%)",
  500: "hsl(120, 59%, 30%)",
  600: "hsl(120, 70%, 25%)",
  700: "hsl(120, 75%, 16%)",
  800: "hsl(120, 84%, 10%)",
  900: "hsl(120, 87%, 6%)",
};

// Amber, and only amber: it used to sit a few degrees off the old gold brand
// and so doubled as a second accent. With the brand on the other side of the
// wheel this ramp is free to mean one thing - caution.
const orange = {
  50: "hsl(38, 100%, 97%)",
  100: "hsl(38, 92%, 90%)",
  200: "hsl(38, 94%, 80%)",
  300: "hsl(38, 90%, 65%)",
  400: "hsl(38, 90%, 45%)",
  500: "hsl(38, 90%, 38%)",
  600: "hsl(38, 91%, 28%)",
  700: "hsl(38, 94%, 21%)",
  800: "hsl(38, 95%, 16%)",
  900: "hsl(38, 93%, 12%)",
};

export const red = {
  50: "hsl(0, 100%, 97%)",
  100: "hsl(0, 92%, 90%)",
  200: "hsl(0, 94%, 80%)",
  300: "hsl(0, 90%, 65%)",
  400: "hsl(0, 90%, 40%)",
  500: "hsl(0, 90%, 30%)",
  600: "hsl(0, 91%, 25%)",
  700: "hsl(0, 94%, 18%)",
  800: "hsl(0, 95%, 12%)",
  900: "hsl(0, 93%, 6%)",
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: colors.prime,
        dark: brand[700],
        // The brand fills are deep blue, so what sits on them is white - the
        // same pairing the wordmark uses.
        contrastText: colors.white,
      },
      secondary: {
        main: colors.grey,
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[900],
      },
      warning: {
        light: orange[300],
        main: colors.orange,
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: colors.red,
        dark: red[800],
      },
      // A green that reads on white: the neon #36FF00 is 1.4:1 there, and
      // it is what the profit lines and the crash chips are printed in.
      success: {
        light: green[300],
        main: green[500],
        dark: green[800],
      },
      grey: {
        main: colors.grey,
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        default: colors.bgLight,
        paper: colors.white,
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
      },
      baseShadow:
        "hsla(215, 32%, 12%, 0.07) 0px 4px 16px 0px, hsla(215, 28%, 18%, 0.07) 0px 8px 16px -5px",
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: colors.white,
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      secondary: {
        main: colors.grey,
      },
      info: {
        contrastText: gray[50],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      // The game's own win green, which the neon was standing in for in the
      // light scheme; the deep green[500] is 2.5:1 on the night navy.
      success: {
        light: green[300],
        main: colors.brightGreen,
        dark: green[500],
      },
      grey: {
        main: colors.grey,
        ...gray,
      },
      divider: colors.borderDark,
      background: {
        default: colors.bgDark,
        paper: colors.layer1Dark,
      },
      text: {
        primary: "#E6EEF9",
        // gray[400] was 3.9:1 on the panels; this clears WCAG AA (4.5:1) on bg, layer1 and layer2.
        secondary: "#8196b6",
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow:
        "hsla(215, 45%, 3%, 0.7) 0px 4px 16px 0px, hsla(215, 40%, 5%, 0.8) 0px 8px 16px -5px",
    },
  },
};

export const typography = {
  fontFamily: "Inter, sans-serif",
  h1: {
    fontSize: defaultTheme.typography.pxToRem(40),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(32),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(32),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

const defaultShadows = [
  "none",
  "var(--template-palette-baseShadow)",
  ...defaultTheme.shadows.slice(2),
] as unknown as Shadows;
export const shadows = defaultShadows;
